import { useCallback, useContext, useMemo } from "react";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import { parseQueries } from "app/utils/routerUtils";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import IconBack from "app/pages/.shared/static/icons/IconBack";
import RelativeLink from "app/pages/.shared/RelativeLink";
import Paragraphs from "app/pages/.shared/Paragraphs";
import LogoBrandContainer from "app/pages/.shared/LogoBrand/LogoBrandContainer";
import AppGlobalsContext from "app/AppGlobalsContext";
import { BRANDS } from "app/constants";
import Separator from "app/pages/.shared/Separator";
import AuthLayerContainer from "app/pages/Auth/AuthLayer/AuthLayerContainer";
import IconPictoChevron from "app/pages/.shared/IconPictoChevron";
import "./AuthBookingLayer.scss";

const AuthBookingLayer = ({
	fallbackRedirectUrl = "/",
	showSigninByDefault = true,
	bookingAuthLandingText = {},
}) => {
	const {
		signinTitle,
		signinDescription,
		signupTitle,
		signupDescription,
	} = bookingAuthLandingText;
	const navigate = useNavigate();
	const { search } = useLocation();
	const query = parseQueries(search);

	const onAuthSuccess = useCallback(() => {
		if (query.onauthsuccess) {
			navigate(query.onauthsuccess, { replace: true });
		} else {
			navigate("/");
		}
	}, [query.onauthsuccess]);

	const { brand } = useContext(AppGlobalsContext);

	const isBrandEH = brand === BRANDS.EH || brand === BRANDS.EK;

	const authFooter = useMemo(
		() => (
			<div className="auth-booking-layer__footer">
				<RelativeLink className="nav__item-link" to={{ pathname: "/booking-search" }}>
					<FormattedMessage id={"booking.auth.footer"} />
					<IconPictoChevron />
				</RelativeLink>
			</div>
		),
		[bookingAuthLandingText, fallbackRedirectUrl]
	);

	const signinMarketingBlock = useMemo(
		() => (
			<div className="booking-auth__marketing">
				<div className="booking-auth__marketing-content">
					{signinTitle && signinTitle.length > 0 ? (
						<h2 className="booking-auth__marketing-title">
							<Paragraphs paragraphs={signinTitle} enableHTMLFormatting />
						</h2>
					) : (
						<div className="booking-auth__marketing-default">
							<FormattedMessage
								id="auth.from.payment.headline.signin"
								tagName="div"
							/>
						</div>
					)}
					{signinDescription && signinDescription.length > 0 && (
						<div className="booking-auth__marketing-description">
							<Paragraphs paragraphs={signinDescription} enableHTMLFormatting />
						</div>
					)}
				</div>
			</div>
		),
		[bookingAuthLandingText]
	);

	const signupMarketingBlock = useMemo(
		() => (
			<div className="booking-auth__marketing">
				<div className="booking-auth__marketing-content">
					{signupTitle && signupTitle.length > 0 ? (
						<h2 className="booking-auth__marketing-title">
							<Paragraphs paragraphs={signupTitle} enableHTMLFormatting />
						</h2>
					) : (
						<div className="booking-auth__marketing-default">
							<FormattedMessage
								id="auth.from.payment.headline.signup"
								tagName="div"
							/>
						</div>
					)}
					{signupDescription && signupDescription.length > 0 ? (
						<div className="booking-auth__marketing-description">
							<Paragraphs paragraphs={signupDescription} enableHTMLFormatting />
						</div>
					) : (
						<div className="booking-auth__marketing-default">
							<FormattedMessage id="auth.from.payment.headline.3" tagName="div" />
							<FormattedMessage id="auth.from.payment.headline.4" tagName="div" />
							<Separator design="middle" />
						</div>
					)}
				</div>
			</div>
		),
		[bookingAuthLandingText]
	);

	return (
		<div className="auth-booking-layer" data-testid="auth-layer">
			<div className="auth-booking-layer__header">
				<div
					className="auth-booking-layer__header-back"
					onClick={() => {
						window.history.back();
					}}
				>
					<IconBack />
				</div>
				<RelativeLink
					to={{ pathname: fallbackRedirectUrl }}
					className={classNames({
						"auth-booking-layer__header__logo": true,
						"auth-booking-layer__header__logo-small": isBrandEH,
					})}
				>
					<LogoBrandContainer />
				</RelativeLink>
			</div>
			<div className="booking-auth">
				<div className="booking-auth__content">
					<AuthLayerContainer
						brand={brand}
						isDefaultSignin={showSigninByDefault}
						onAuthSuccess={onAuthSuccess}
						signinMarketingBlock={signinMarketingBlock}
						signupMarketingBlock={signupMarketingBlock}
						signupFooter={authFooter}
						signinFooter={authFooter}
					/>
				</div>
			</div>
		</div>
	);
};

AuthBookingLayer.propTypes = {
	fallbackRedirectUrl: PropTypes.string,
	showSigninByDefault: PropTypes.bool,
	bookingAuthLandingText: PropTypes.object,
};

export default AuthBookingLayer;
